import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Formik, Form } from 'formik';
import * as actions from './redux/actions';
import CustomLoader from '../common/CustomLoader';
import CollectionHeaderNav from './CollectionHeaderNav';
import types from '../../common/formElements/CollectionSectionType';
import CommonPageComponent from '../../common/ui/CommonPageComponent';
import ParentCard from '../common/ParentCard';
import CustomSelect from '../home/collections/CustomSelect';
import voca from 'voca';

class PermissionLevelSecurity extends Component {
  static propTypes = {
    collections: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
  };

  state = {
    page: 0,
    permissionOptions: [
      { value: 'allowed', label: 'Allowed' },
      { value: 'not_allowed', label: 'Not Allowed' },
      { value: 'restricted', label: 'Restricted' },
    ],
  };

  componentDidMount() {
    const { projectId, collectionId, version } = this.props.match.params;
    const { page } = this.state;
    this.triggerLoadCollectionItems(page);
    this.props.actions.loadProjectCollections({ projectId, version });
    this.props.actions.loadProjectCollectionDetail({ projectId, collectionId, version });
    this.props.actions.loadProjectPlugins({ projectId, version });
  }

  handleSubmit = (values) => {
    const { collections, match, actions } = this.props;
    const { selectedCollection } = collections;
    const { projectId, version } = match.params;

    const transformedValues = Object.keys(values)
      .filter((key) => !key.endsWith('_allowedPermissions'))
      .map((key) => {
        const fieldObject = {
          fieldName: key,
          permission: null,
          allowedPermissions: [],
        };

        if (typeof values[key] === 'string') {
          fieldObject.permission = values[key];
        } else if (typeof values[key] === 'object' && values[key] !== null) {
          fieldObject.permission = values[key].value || null;
          const allowedPermissionsKey = `${key}_allowedPermissions`;
          fieldObject.allowedPermissions = Array.isArray(values[allowedPermissionsKey])
            ? values[allowedPermissionsKey].map((permission) => permission.value)
            : [];
        }

        return fieldObject;
      });

    const payload = {
      permissionLevelSecurity: transformedValues,
    };
    actions.createPermissionLevelSecurityInCollection({
      projectId,
      version,
      data: payload,
      selectedCollection,
    });
  };

  triggerLoadCollectionItems = (page, searchTerm = '') => {
    const { match, actions } = this.props;
    const { projectId, version } = match.params;
    const query = { projectId, collectionName: 'permission', page, version };
    if (searchTerm) {
      query.searchTerm = searchTerm;
    }
    actions.loadCollectionItems(query);
  };

  getInitialValues = (selectedCollection) => {
    return selectedCollection.fields.reduce((acc, field) => {
      const fieldName = field.fieldName;
      const securitySetting = selectedCollection.permissionLevelSecurity.find(
        (item) => item.fieldName === fieldName,
      );

      if (securitySetting) {
        acc[fieldName] = {
          value: securitySetting.permission,
          label:
            securitySetting.permission === 'not_allowed'
              ? 'Not Allowed'
              : voca.titleCase(securitySetting.permission),
        };
        acc[`${fieldName}_allowedPermissions`] = securitySetting.allowedPermissions.map((perm) => ({
          label: perm,
          value: perm,
        }));
      } else {
        acc[fieldName] = { value: 'allowed', label: 'Allowed' };
        acc[`${fieldName}_allowedPermissions`] = [];
      }

      return acc;
    }, {});
  };

  render() {
    const { collections, match } = this.props;
    const { selectedCollection } = collections;
    const { projectId, version } = match.params;
    const { loadProjectCollectionsPending, loadProjectCollectionDetailPending, items, plugins } =
      collections;
    const { permissionOptions } = this.state;
    const restrictionOptions = items.map((item) => ({
      label: item.name,
      value: item.name,
    }));
    const hasPermissionPlugin = plugins.some((plugin) => plugin.code === 'PERMISSIONS_MANAGE');

    if (loadProjectCollectionsPending || loadProjectCollectionDetailPending) {
      return (
        <CommonPageComponent>
          <CollectionHeaderNav
            label={types.PERMISSION_LEVEL_SECURITY}
            projectId={projectId}
            version={version}
            selectedCollection={selectedCollection}
            loadProjectCollectionsPending={loadProjectCollectionsPending}
            loadProjectCollectionDetailPending={loadProjectCollectionDetailPending}
          />
          <div className="text-center">
            <CustomLoader
              loading={loadProjectCollectionsPending || loadProjectCollectionDetailPending}
              size="20px"
              color="#303453"
            />
          </div>
        </CommonPageComponent>
      );
    }

    if (!hasPermissionPlugin) {
      return (
        <CommonPageComponent>
          <CollectionHeaderNav
            label={types.PERMISSION_LEVEL_SECURITY}
            projectId={projectId}
            version={version}
            selectedCollection={selectedCollection}
            loadProjectCollectionsPending={loadProjectCollectionsPending}
            loadProjectCollectionDetailPending={loadProjectCollectionDetailPending}
          />
          <ParentCard
            icon="permissionLevelSecurity"
            header={`Permission Level Security ${
              selectedCollection && ` For ${selectedCollection.collectionTitle}`
            }`}
            subtitle="Create customized permission level security in a collection."
          >
            <div className="text-center">
              <h5>Please install the Permissions plugin to manage security settings.</h5>
            </div>
          </ParentCard>
        </CommonPageComponent>
      );
    }

    const initialValues = this.getInitialValues(selectedCollection);
    return (
      <CommonPageComponent>
        <CollectionHeaderNav
          label={types.PERMISSION_LEVEL_SECURITY}
          projectId={projectId}
          version={version}
          selectedCollection={selectedCollection}
          loadProjectCollectionsPending={loadProjectCollectionsPending}
          loadProjectCollectionDetailPending={loadProjectCollectionDetailPending}
        />
        {selectedCollection && (
          <ParentCard
            icon="permissionLevelSecurity"
            header={`Permission Level Security ${
              selectedCollection && ` For ${selectedCollection.collectionTitle}`
            }`}
            subtitle="Create customized permission level security in a collection."
          >
            <Formik initialValues={initialValues} onSubmit={this.handleSubmit}>
              {({ setFieldValue, setFieldTouched, values }) => {
                return (
                  <Form className="form-horizontal">
                    <div className="row">
                      <div className="col-6">
                        <h5>Collection Fields</h5>
                      </div>
                      <div className="col-6">
                        <h5>Permissions</h5>
                      </div>
                    </div>
                    {selectedCollection.fields.map((field) => {
                      return (
                        <div className="row mx-auto mb-3" key={field.fieldName}>
                          <div className="col-md-3 col-lg-3 col-sm-12 col-xs-12">
                            <span className="input-group-text">{field.fieldTitle.en}</span>
                          </div>
                          <div className="col-md-6 col-lg-6">
                            <CustomSelect
                              name={field.fieldName}
                              isMulti={false}
                              value={values[field.fieldName]}
                              options={permissionOptions}
                              onChange={setFieldValue}
                              onBlur={setFieldTouched}
                              placeholder="Select Permission"
                            />
                          </div>
                          {values[field.fieldName] &&
                            values[field.fieldName].value === 'restricted' && (
                              <div className="col-md-3 col-lg-3 col-sm-12 col-xs-12">
                                <CustomSelect
                                  name={`${field.fieldName}_allowedPermissions`}
                                  isMulti={true}
                                  value={values[`${field.fieldName}_allowedPermissions`]}
                                  options={restrictionOptions}
                                  onChange={setFieldValue}
                                  onBlur={setFieldTouched}
                                  placeholder="Select Allowed Permissions"
                                />
                              </div>
                            )}
                        </div>
                      );
                    })}

                    <div className="text-right mt-3">
                      <button type="submit" className="btn btn-theme-submit btn-md">
                        {selectedCollection.permissionLevelSecurity &&
                        selectedCollection.permissionLevelSecurity.length ? (
                          <>
                            <i className="wb webconnect-save-regular"></i>&nbsp;Update&nbsp;
                          </>
                        ) : (
                          <>
                            <i className="wb webconnect-plus"></i>&nbsp;Save&nbsp;
                          </>
                        )}
                        <CustomLoader loading={false} />
                      </button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </ParentCard>
        )}
      </CommonPageComponent>
    );
  }
}

function mapStateToProps(state) {
  return {
    collections: state.collections,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(PermissionLevelSecurity);
